import React, { useState } from 'react';
import {
  Award, Download, Share2, ExternalLink, Calendar, Clock,
  Star, Users, Trophy, Medal, Crown, Bookmark, Filter,
  Search, Grid3x3, List, Eye, Copy, Linkedin, Twitter,
  Facebook, Mail, FileText, CheckCircle, Globe, Link2,
  QrCode, Printer, MoreVertical, RefreshCw, Plus, Info
} from 'lucide-react';

interface Certificate {
  id: string;
  title: string;
  courseName: string;
  instructor: string;
  issuer: string;
  issueDate: string;
  expiryDate?: string;
  credentialId: string;
  verificationUrl: string;
  badgeUrl?: string;
  certificateUrl?: string;
  grade: string;
  score: number;
  totalHours: number;
  skills: string[];
  type: 'completion' | 'achievement' | 'specialization' | 'professional';
  category: string;
  status: 'active' | 'expired' | 'revoked';
  isPublic: boolean;
  shareCount: number;
  validationHash: string;
  blockchainVerified: boolean;
}

export default function LearnerCertificates() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const certificates: Certificate[] = [
    {
      id: '1',
      title: 'Advanced React Development Certificate',
      courseName: 'Advanced React Development',
      instructor: 'Sarah Chen',
      issuer: 'Academy',
      issueDate: '2024-01-28',
      credentialId: 'CERT-REACT-2024-001',
      verificationUrl: 'https://verify.example.com/cert/CERT-REACT-2024-001',
      certificateUrl: '/certificates/react-advanced-001.pdf',
      grade: 'A+ (95%)',
      score: 95,
      totalHours: 24,
      skills: ['React Hooks', 'Performance Optimization', 'Testing', 'State Management'],
      type: 'completion',
      category: 'Development',
      status: 'active',
      isPublic: true,
      shareCount: 12,
      validationHash: 'e8a3c9b2f5d4a6e1b9c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0d1e2f3a4',
      blockchainVerified: true
    },
    {
      id: '2',
      title: 'Data Science Fundamentals Certificate',
      courseName: 'Data Science Fundamentals',
      instructor: 'Dr. Michael Roberts',
      issuer: 'Academy',
      issueDate: '2024-01-15',
      credentialId: 'CERT-DS-2024-007',
      verificationUrl: 'https://verify.example.com/cert/CERT-DS-2024-007',
      certificateUrl: '/certificates/data-science-007.pdf',
      grade: 'A (88%)',
      score: 88,
      totalHours: 32,
      skills: ['Python', 'Pandas', 'Data Visualization', 'Statistics'],
      type: 'completion',
      category: 'Data Science',
      status: 'active',
      isPublic: true,
      shareCount: 8,
      validationHash: 'a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2',
      blockchainVerified: true
    },
    {
      id: '3',
      title: 'Leadership Excellence Certificate',
      courseName: 'Leadership Excellence Program',
      instructor: 'Jennifer Williams',
      issuer: 'Academy',
      issueDate: '2024-01-10',
      expiryDate: '2027-01-10',
      credentialId: 'CERT-LEAD-2024-023',
      verificationUrl: 'https://verify.example.com/cert/CERT-LEAD-2024-023',
      certificateUrl: '/certificates/leadership-023.pdf',
      grade: 'A (92%)',
      score: 92,
      totalHours: 16,
      skills: ['Team Management', 'Communication', 'Strategic Thinking', 'Decision Making'],
      type: 'professional',
      category: 'Management',
      status: 'active',
      isPublic: false,
      shareCount: 0,
      validationHash: 'c7d8e9f0a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8',
      blockchainVerified: true
    },
    {
      id: '4',
      title: 'Python Programming Specialist',
      courseName: 'Python for Beginners',
      instructor: 'Alex Kumar',
      issuer: 'Academy',
      issueDate: '2024-01-05',
      credentialId: 'CERT-PY-2024-156',
      verificationUrl: 'https://verify.example.com/cert/CERT-PY-2024-156',
      certificateUrl: '/certificates/python-156.pdf',
      grade: 'A+ (98%)',
      score: 98,
      totalHours: 20,
      skills: ['Python Basics', 'OOP', 'Data Structures', 'Error Handling'],
      type: 'specialization',
      category: 'Programming',
      status: 'active',
      isPublic: true,
      shareCount: 25,
      validationHash: 'f1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2',
      blockchainVerified: true
    },
    {
      id: '5',
      title: 'Digital Marketing Achievement Badge',
      courseName: 'Digital Marketing Mastery',
      instructor: 'Lisa Anderson',
      issuer: 'Academy',
      issueDate: '2023-12-20',
      expiryDate: '2025-12-20',
      credentialId: 'CERT-DM-2023-089',
      verificationUrl: 'https://verify.example.com/cert/CERT-DM-2023-089',
      badgeUrl: '/badges/digital-marketing-089.png',
      grade: 'B+ (82%)',
      score: 82,
      totalHours: 28,
      skills: ['SEO', 'Social Media Marketing', 'Analytics', 'Campaign Management'],
      type: 'achievement',
      category: 'Marketing',
      status: 'active',
      isPublic: true,
      shareCount: 6,
      validationHash: 'b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0',
      blockchainVerified: false
    }
  ];

  const stats = {
    totalCertificates: certificates.length,
    activeCertificates: certificates.filter(c => c.status === 'active').length,
    blockchainVerified: certificates.filter(c => c.blockchainVerified).length,
    totalShares: certificates.reduce((sum, c) => sum + c.shareCount, 0),
    averageScore: Math.round(certificates.reduce((sum, c) => sum + c.score, 0) / certificates.length),
    totalHours: certificates.reduce((sum, c) => sum + c.totalHours, 0)
  };

  const filteredCertificates = certificates.filter(cert => {
    const matchesSearch = cert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          cert.courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          cert.skills.some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = filterCategory === 'all' || cert.category === filterCategory;
    const matchesType = filterType === 'all' || cert.type === filterType;
    return matchesSearch && matchesCategory && matchesType;
  });

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'completion': return 'text-blue-600 bg-blue-50';
      case 'achievement': return 'text-green-600 bg-green-50';
      case 'specialization': return 'text-purple-600 bg-purple-50';
      case 'professional': return 'text-indigo-600 bg-indigo-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'completion': return <Award className="w-4 h-4" />;
      case 'achievement': return <Medal className="w-4 h-4" />;
      case 'specialization': return <Star className="w-4 h-4" />;
      case 'professional': return <Crown className="w-4 h-4" />;
      default: return <Trophy className="w-4 h-4" />;
    }
  };

  const handleShare = (certificate: Certificate) => {
    setSelectedCertificate(certificate);
    setShowShareModal(true);
  };

  const handleViewDetails = (certificate: Certificate) => {
    setSelectedCertificate(certificate);
    setShowDetailsModal(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Toast notification would go here
  };

  const isExpired = (expiryDate?: string) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-yellow-500 to-brand-orange-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <Award className="w-7 h-7" />
              Digital Certificates & Credentials
            </h1>
            <p className="text-yellow-100">
              Manage and share your professional certificates and achievements
            </p>
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors">
              <RefreshCw className="w-5 h-5" />
              <span className="hidden sm:inline">Sync</span>
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors">
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Add External</span>
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-white text-orange-600 rounded-lg hover:bg-orange-50 transition-colors font-medium">
              <Globe className="w-5 h-5" />
              <span className="hidden sm:inline">Public Portfolio</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Award className="w-6 h-6 mx-auto mb-2 text-yellow-200" />
            <p className="text-2xl font-bold">{stats.totalCertificates}</p>
            <p className="text-xs text-yellow-100">Total Certificates</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-200" />
            <p className="text-2xl font-bold">{stats.activeCertificates}</p>
            <p className="text-xs text-yellow-100">Active</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Globe className="w-6 h-6 mx-auto mb-2 text-blue-200" />
            <p className="text-2xl font-bold">{stats.blockchainVerified}</p>
            <p className="text-xs text-yellow-100">Verified</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Star className="w-6 h-6 mx-auto mb-2 text-pink-200" />
            <p className="text-2xl font-bold">{stats.averageScore}%</p>
            <p className="text-xs text-yellow-100">Avg Score</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search certificates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange-500"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange-500"
          >
            <option value="all">All Categories</option>
            <option value="Development">Development</option>
            <option value="Data Science">Data Science</option>
            <option value="Management">Management</option>
            <option value="Programming">Programming</option>
            <option value="Marketing">Marketing</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange-500"
          >
            <option value="all">All Types</option>
            <option value="completion">Completion</option>
            <option value="achievement">Achievement</option>
            <option value="specialization">Specialization</option>
            <option value="professional">Professional</option>
          </select>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-orange-100 text-orange-600' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <Grid3x3 className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-orange-100 text-orange-600' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Certificates */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6">
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCertificates.map((certificate) => (
                <div key={certificate.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all relative">
                  {certificate.blockchainVerified && (
                    <div className="absolute top-4 right-4">
                      <div className="p-1 bg-green-100 rounded-full" title="Blockchain Verified">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                    </div>
                  )}
                  
                  <div className="text-center mb-4">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${
                      certificate.type === 'completion' ? 'from-blue-500 to-indigo-600' :
                      certificate.type === 'achievement' ? 'from-green-500 to-emerald-600' :
                      certificate.type === 'specialization' ? 'from-purple-500 to-pink-600' :
                      'from-indigo-500 to-purple-600'
                    } mb-3`}>
                      <div className="text-white">
                        {getTypeIcon(certificate.type)}
                      </div>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">{certificate.title}</h3>
                    <p className="text-sm text-gray-600">{certificate.courseName}</p>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Issued by:</span>
                      <span className="font-medium text-gray-900">{certificate.issuer}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Date:</span>
                      <span className="font-medium text-gray-900">{new Date(certificate.issueDate).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Grade:</span>
                      <span className="font-medium text-gray-900">{certificate.grade}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Hours:</span>
                      <span className="font-medium text-gray-900">{certificate.totalHours}h</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(certificate.type)}`}>
                      {certificate.type}
                    </span>
                    {certificate.expiryDate && (
                      <span className={`text-xs ${isExpired(certificate.expiryDate) ? 'text-red-600' : 'text-gray-500'}`}>
                        {isExpired(certificate.expiryDate) ? 'Expired' : `Expires ${new Date(certificate.expiryDate).toLocaleDateString()}`}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1 mb-4">
                    {certificate.skills.slice(0, 3).map((skill, index) => (
                      <span key={index} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                        {skill}
                      </span>
                    ))}
                    {certificate.skills.length > 3 && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                        +{certificate.skills.length - 3}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleViewDetails(certificate)}
                      className="flex-1 px-3 py-1.5 bg-brand-orange-600 text-white rounded-lg hover:bg-brand-orange-700 text-sm font-medium"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => handleShare(certificate)}
                      className="p-1.5 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg"
                      title="Share"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => window.open(certificate.certificateUrl || certificate.verificationUrl)}
                      className="p-1.5 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCertificates.map((certificate) => (
                <div key={certificate.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                  <div className="flex items-center gap-6">
                    <div className={`flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${
                      certificate.type === 'completion' ? 'from-blue-500 to-indigo-600' :
                      certificate.type === 'achievement' ? 'from-green-500 to-emerald-600' :
                      certificate.type === 'specialization' ? 'from-purple-500 to-pink-600' :
                      'from-indigo-500 to-purple-600'
                    }`}>
                      <div className="text-white">
                        {getTypeIcon(certificate.type)}
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            {certificate.title}
                            {certificate.blockchainVerified && (
                              <CheckCircle className="w-4 h-4 text-green-600" title="Blockchain Verified" />
                            )}
                          </h3>
                          <p className="text-sm text-gray-600">{certificate.courseName} • {certificate.instructor}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(certificate.type)}`}>
                          {certificate.type}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500">Issued</p>
                          <p className="text-sm font-medium text-gray-900">{new Date(certificate.issueDate).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Grade</p>
                          <p className="text-sm font-medium text-gray-900">{certificate.grade}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Hours</p>
                          <p className="text-sm font-medium text-gray-900">{certificate.totalHours}h</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Credential ID</p>
                          <p className="text-sm font-medium text-gray-900 font-mono">{certificate.credentialId}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1 mb-4">
                        {certificate.skills.map((skill, index) => (
                          <span key={index} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewDetails(certificate)}
                        className="px-4 py-2 bg-brand-orange-600 text-white rounded-lg hover:bg-brand-orange-700 font-medium"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => handleShare(certificate)}
                        className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg"
                        title="Share"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => window.open(certificate.certificateUrl || certificate.verificationUrl)}
                        className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {filteredCertificates.length === 0 && (
            <div className="text-center py-12">
              <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No certificates found</h3>
              <p className="text-gray-600">Complete more courses to earn certificates, or try adjusting your search criteria.</p>
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && selectedCertificate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 common-modal-overlay">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Share Certificate</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="text-center mb-6">
              <div className={`w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-r ${
                selectedCertificate.type === 'completion' ? 'from-blue-500 to-indigo-600' :
                selectedCertificate.type === 'achievement' ? 'from-green-500 to-emerald-600' :
                selectedCertificate.type === 'specialization' ? 'from-purple-500 to-pink-600' :
                'from-indigo-500 to-purple-600'
              } flex items-center justify-center text-white`}>
                {getTypeIcon(selectedCertificate.type)}
              </div>
              <h4 className="font-medium text-gray-900">{selectedCertificate.title}</h4>
              <p className="text-sm text-gray-600 mt-1">{selectedCertificate.courseName}</p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <button className="flex items-center justify-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                <Linkedin className="w-5 h-5 text-blue-600" />
                LinkedIn
              </button>
              <button className="flex items-center justify-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                <Twitter className="w-5 h-5 text-blue-400" />
                Twitter
              </button>
              <button className="flex items-center justify-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                <Facebook className="w-5 h-5 text-blue-800" />
                Facebook
              </button>
              <button className="flex items-center justify-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                <Mail className="w-5 h-5 text-gray-600" />
                Email
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Public Link</label>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="text"
                    value={selectedCertificate.verificationUrl}
                    readOnly
                    className="flex-1 bg-transparent text-sm text-gray-600 outline-none"
                  />
                  <button
                    onClick={() => copyToClipboard(selectedCertificate.verificationUrl)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Credential ID</label>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="text"
                    value={selectedCertificate.credentialId}
                    readOnly
                    className="flex-1 bg-transparent text-sm text-gray-600 outline-none font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(selectedCertificate.credentialId)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedCertificate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 common-modal-overlay">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-full bg-gradient-to-r ${
                    selectedCertificate.type === 'completion' ? 'from-blue-500 to-indigo-600' :
                    selectedCertificate.type === 'achievement' ? 'from-green-500 to-emerald-600' :
                    selectedCertificate.type === 'specialization' ? 'from-purple-500 to-pink-600' :
                    'from-indigo-500 to-purple-600'
                  } text-white`}>
                    {getTypeIcon(selectedCertificate.type)}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                      {selectedCertificate.title}
                      {selectedCertificate.blockchainVerified && (
                        <CheckCircle className="w-5 h-5 text-green-600" title="Blockchain Verified" />
                      )}
                    </h2>
                    <p className="text-sm text-gray-600">{selectedCertificate.courseName}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Certificate Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Issued by:</span>
                      <span className="font-medium">{selectedCertificate.issuer}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Instructor:</span>
                      <span className="font-medium">{selectedCertificate.instructor}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Issue Date:</span>
                      <span className="font-medium">{new Date(selectedCertificate.issueDate).toLocaleDateString()}</span>
                    </div>
                    {selectedCertificate.expiryDate && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Expires:</span>
                        <span className={`font-medium ${isExpired(selectedCertificate.expiryDate) ? 'text-red-600' : ''}`}>
                          {new Date(selectedCertificate.expiryDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium capitalize">{selectedCertificate.type}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Performance</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Grade:</span>
                      <span className="font-medium">{selectedCertificate.grade}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Score:</span>
                      <span className="font-medium">{selectedCertificate.score}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-medium">{selectedCertificate.totalHours} hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Category:</span>
                      <span className="font-medium">{selectedCertificate.category}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-3">Skills Validated</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedCertificate.skills.map((skill, index) => (
                    <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-3">Verification</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Credential ID:</span>
                    <span className="text-sm font-mono font-medium flex-1">{selectedCertificate.credentialId}</span>
                    <button
                      onClick={() => copyToClipboard(selectedCertificate.credentialId)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Verification URL:</span>
                    <a
                      href={selectedCertificate.verificationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-brand-orange-600 hover:text-brand-orange-700 flex-1"
                    >
                      {selectedCertificate.verificationUrl}
                    </a>
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  </div>

                  {selectedCertificate.blockchainVerified && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-800">This certificate is verified on the blockchain</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Share2 className="w-4 h-4" />
                  <span>Shared {selectedCertificate.shareCount} times</span>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => window.open(selectedCertificate.certificateUrl || selectedCertificate.verificationUrl)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                  >
                    <Download className="w-4 h-4 inline mr-1" />
                    Download
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      setShowShareModal(true);
                    }}
                    className="px-4 py-2 bg-brand-orange-600 text-white rounded-lg hover:bg-brand-orange-700 font-medium"
                  >
                    <Share2 className="w-4 h-4 inline mr-1" />
                    Share
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}